import React, { useEffect, useState } from "react";
import { Field, Formik, Form, ErrorMessage } from "formik";
import * as Yup from "yup";
import loginStyles from "../../Login/Login.module.scss";
import { useParams, useNavigate } from "react-router-dom";
import { useMask } from "@react-input/mask";
import toast from "react-hot-toast";
import { FiAlertCircle } from "react-icons/fi";
import { HiOutlineLightBulb } from "react-icons/hi";
import PasswordStrength from "components/PasswordStrength/PasswordStrength";
import Avatar from "components/Avatar/Avatar";
import { api, createUserAccount, formatUserData } from "services";
import { LoginLayout } from "pages/Login/Login";
import Button, { ButtonWrapper } from "components/Button/Button";
import { validateCPF } from "utils/validateCPF";
import {
  FormControl,
  FormFeedback,
  FormField,
  FormLabel,
  FormRow,
  FormStep,
  FormTerms,
} from "components/Form/Form";
import styles from "./styles.module.scss";

export const checkEmail = async (email) => {
  try {
    return await api.post(`/users/check_email?email=${email}`);
  } catch (error) {
    toast.error(error.response?.data?.error || "Erro ao verificar email");
  }
};

const cpfSchema = Yup.object().shape({
  cpf: Yup.string()
    .required("Campo obrigatório")
    .test("cpf", "CPF Inválido", (value) => validateCPF(value)),
});

const passwordSchema = Yup.object().shape({
  email: Yup.string().email("E-mail inválido").required("Campo obrigatório"),
  password: Yup.string().required("Campo obrigatório"),
  confirmPassword: Yup.string()
    .oneOf([Yup.ref("password"), null], "Ops! As senhas devem ser iguais")
    .required("Campo obrigatório"),
});

const termsSchema = Yup.object().shape({
  terms: Yup.string().required("É necessário aceitar os termos."),
});

export default function StudentSignUp() {
  const navigate = useNavigate();
  const [formValues, setFormValues] = useState({});
  const [showPasswordStrength, setShowPasswordStrength] = useState(false);
  const [passwordIsStrong, setPasswordIsStrong] = useState(false);
  const [errors, setErrors] = useState({});
  const [findedUser, setFindedUser] = useState(null);
  const [loading, setLoading] = useState(false);

  const { formStep, connection, link } = useParams();

  useEffect(() => {
    const pathname = window.location.pathname;

    if (pathname.includes("/aluno/")) {
      const newPath = pathname.replace("/aluno", "");
      navigate(newPath);
    }
  }, []);

  const checkUserCpf = async (cpf) => {
    const cpfLoader = toast.loading("Consultando CPF...");
    setLoading(true);
    const formatedCpf = cpf.replace(/\D/g, "");
    try {
      const { data } = await api.get(`/users/${formatedCpf}`);
      return data;
    } catch (error) {
      return error.response;
    } finally {
      toast.dismiss(cpfLoader);
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!formStep) {
      navigate("/criar-uma-conta/consulta-de-dados");
    }
  }, []);

  const cfpRef = useMask({
    mask: "___.___.___-__",
    replacement: { _: /\d/ },
  });
  const passwordRef = React.createRef();
  const confirmPasswordRef = React.createRef();

  function handleFormValues(values) {
    setFormValues({ ...formValues, ...values });
  }

  const handleNext = async (values, actions) => {
    handleFormValues(values);
    actions.setSubmitting(false);
    setErrors({});

    if (formStep === "consulta-de-dados") {
      const response = await checkUserCpf(values.cpf);

      if (response.status === 409 || response.status === 422) {
        actions.setFieldError("cpf", response.data.error);
        return;
      }
      if (response.status === 404) {
        setFindedUser(null);
      } else {
        const hasCourses = response?.data?.some(
          (inst) => inst.courses && inst.courses.length > 0,
        );
        const classroomIds = response?.data?.flatMap((inst) =>
          inst?.courses?.flatMap((course) =>
            course?.classrooms?.map((c) => c.id),
          ),
        );
        const inepIds = response?.data?.map((inst) => inst.inep);

        setFormValues({
          ...formValues,
          cpf: values.cpf,
          email: response.institutional_email ?? response.email,
          serie: response.connections,
          ...(hasCourses
            ? {
                classroom_ids: classroomIds,
              }
            : { institution_codes: inepIds }),
        });
        setFindedUser(response);
      }
      navigate(
        link && connection
          ? `/criar-uma-conta/${link}/${connection}/revisar-dados`
          : "/criar-uma-conta/revisar-dados",
      );
    }

    if (formStep === "senha") {
      const email = values.email;

      setLoading(true);
      const emailLoader = toast.loading("Verificando email...");
      try {
        const response = await checkEmail(email);

        const { found } = response.data;

        if (found) {
          actions.setFieldError(
            "email",
            "Este email já está cadastrado. Termine seu cadastro com um email diferente ou solicite nova senha para este email.",
          );
          return;
        }
        if (link && connection) {
          navigate(`/criar-uma-conta/${link}/${connection}/termos`);
        } else {
          navigate("/criar-uma-conta/termos");
        }
      } catch (error) {
        toast.error("Erro ao verificar email");
      } finally {
        toast.dismiss(emailLoader);
        setLoading(false);
      }
    }

    if (formStep === "termos") {
      const formatedUser = formatUserData({
        ...formValues,
        name: findedUser ? findedUser.name : formValues.name,
        profile: findedUser ? findedUser.profile : "student",
        ...(link && { link }),
      });
      const termsLoader = toast.loading("Criando sua conta...");
      setLoading(true);
      try {
        const response = await createUserAccount(formatedUser);
        if (response) {
          navigate("/criar-uma-conta/conta-criada");
        }
      } catch (error) {
        toast.error(error.response?.data?.error || "Erro ao criar conta");
      } finally {
        toast.dismiss(termsLoader);
        setLoading(false);
      }
    }
  };

  const handleBackButton = () => {
    if (formStep === "consulta-de-dados")
      return link && connection ? `/login/${link}/${connection}` : "/login";
    if (formStep === "revisar-dados")
      return link && connection
        ? `/criar-uma-conta/${link}/${connection}/consulta-de-dados`
        : "/criar-uma-conta/consulta-de-dados";
    if (formStep === "senha") {
      return link && connection
        ? `/criar-uma-conta/${link}/${connection}/revisar-dados`
        : "/criar-uma-conta/revisar-dados";
    }
    if (formStep === "termos") {
      if (link && connection) {
        return `/criar-uma-conta/${link}/${connection}/senha`;
      }
      return "/criar-uma-conta/senha";
    }
  };

  const hasCourses = findedUser?.data?.some(
    (inst) => inst.courses && inst.courses.length > 0,
  );

  const academicList =
    findedUser &&
    findedUser?.data?.flatMap((institution) =>
      institution?.courses?.map((course) => ({
        institution: institution?.institution,
        courseTitle: course?.title,
        classrooms: course?.classrooms?.map((c) => c?.title).join(", "),
      })),
    );

  const list = hasCourses ? academicList : findedUser?.data;

  const getUserProfileName = (profile) => {
    switch (profile) {
      case "student":
        return "Aluno";
      case "teacher":
        return "Professor";
      case "diretor":
        return "Diretor";
      case "coordenador_pedagogico":
        return "Coordenador Pedagógico";
      default:
        return "";
    }
  };

  const currentYear = new Date().getFullYear();

  return (
    <LoginLayout back={handleBackButton()}>
      <FormStep show={formStep === "consulta-de-dados"}>
        <Formik
          initialValues={{ cpf: formValues.cpf ?? "" }}
          validationSchema={cpfSchema}
          onSubmit={handleNext}
        >
          {(props) => (
            <Form>
              <div className={loginStyles.body}>
                <div className={loginStyles.textGroup}>
                  <h1 className={loginStyles.title}>
                    Vamos consultar seu dados na <strong>Rede SEDUC</strong>
                  </h1>
                </div>

                <FormRow>
                  <FormLabel htmlFor="cpf">Digite seu CPF</FormLabel>
                  <FormControl
                    id="cpf"
                    name="cpf"
                    type="text"
                    value={props.values.cpf}
                    invalid={props.errors.cpf && props.touched.cpf}
                    placeholder="000.000.000-00"
                    ref={cfpRef}
                  />
                  <ErrorMessage name="cpf">
                    {(msg) => <FormFeedback invalid={true}>{msg}</FormFeedback>}
                  </ErrorMessage>
                </FormRow>
              </div>

              <ButtonWrapper>
                <Button
                  modifiers={["primary"]}
                  type="submit"
                  style={{ minWidth: 134 }}
                  disabled={props.isSubmitting || !props.values.cpf || loading}
                >
                  Próximo
                </Button>
              </ButtonWrapper>
            </Form>
          )}
        </Formik>
      </FormStep>
      <FormStep show={formStep === "revisar-dados"}>
        <Formik>
          {(props) => (
            <Form>
              <div className={loginStyles.body}>
                <div className={loginStyles.textGroup}>
                  <h1 className={loginStyles.title}>
                    {findedUser ? (
                      <p className="text-primary-dark">
                        Esses foram os dados encontrados relativos ao{" "}
                        <strong>CPF {formValues.cpf}</strong>. Selecione o que
                        mais se aproxima da sua realidade
                      </p>
                    ) : (
                      <p className="text-primary-dark">
                        Que pena! Seus dados{" "}
                        <strong>não foram encontrados no iSeduc.</strong>
                      </p>
                    )}
                  </h1>
                </div>

                <FormRow>
                  <div
                    className={styles["user-card"]}
                    style={{
                      background: "linear-gradient(#f9fcfd 0%, #ecf2f7 100%)",
                    }}
                  >
                    <div className={styles["user-avatar"]}>
                      {findedUser ? (
                        <Avatar
                          className={`avatar ${styles["user-avatar--img"]}`}
                          src={null}
                        />
                      ) : (
                        <div
                          className={`avatar ${styles["user-avatar--img"]} ${styles["user-avatar--not-finded"]}`}
                        >
                          ?
                        </div>
                      )}
                      {findedUser &&
                        getUserProfileName(findedUser.profile) !== "" && (
                          <span className={` ${styles["user-avatar--badge"]}`}>
                            {getUserProfileName(findedUser.profile)}
                          </span>
                        )}
                    </div>
                    <div className={styles["user-data"]}>
                      <p className={styles["user-data-name"]}>
                        {findedUser
                          ? findedUser.name
                          : "Usuário não encontrado"}
                      </p>
                      <p
                        className={`mb-0 ${
                          findedUser
                            ? styles["user-data-text--disabled"]
                            : styles["user-data-error"]
                        }`}
                      >
                        CPF: {findedUser ? findedUser.cpf : "não encontrado"}
                      </p>
                      {findedUser && findedUser.institutional_email && (
                        <p className={styles["user-data-text--disabled"]}>
                          Email: {findedUser.institutional_email}
                        </p>
                      )}

                      <div className={styles["user-academic-data"]}>
                        {findedUser &&
                          findedUser.data &&
                          list.map((item, index) => (
                            <div style={{ marginBottom: "1.5rem" }} key={index}>
                              <p>
                                <span style={{ fontWeight: "bold" }}>
                                  Escola:
                                </span>{" "}
                                {item.institution}
                              </p>
                              {item.courseTitle && (
                                <p>
                                  <span style={{ fontWeight: "bold" }}>
                                    Curso:
                                  </span>{" "}
                                  {item.courseTitle}
                                </p>
                              )}
                              {item.classrooms && (
                                <p>
                                  <span style={{ fontWeight: "bold" }}>
                                    Turma:
                                  </span>{" "}
                                  {item.classrooms}
                                </p>
                              )}
                            </div>
                          ))}
                      </div>
                    </div>
                  </div>
                  <div className={styles["info"]}>
                    <span
                      className={`${styles["alert-icon"]} ${styles["alert-icon--warning"]}`}
                    >
                      <FiAlertCircle />
                    </span>
                    <div>
                      <p className="mb-0">
                        {findedUser ? (
                          <>
                            <b>Atenção: </b>caso você discorde, atualize seus
                            dados junto à gestão escolar. Para continuar
                            acessando os conteúdos de {currentYear}, é só pedir
                            para a gestão da sua escola atualizar seus dados.
                          </>
                        ) : (
                          "Entre em contato com sua escola para verificar seu cadastro no iSeduc."
                        )}
                      </p>
                    </div>
                  </div>
                </FormRow>
              </div>

              <ButtonWrapper>
                {findedUser ? (
                  <>
                    <Button
                      modifiers={["success"]}
                      anchor
                      to={
                        link && connection
                          ? `/criar-uma-conta/${link}/${connection}/senha`
                          : "/criar-uma-conta/senha"
                      }
                    >
                      Sim. Vamos seguir
                    </Button>
                    <Button
                      modifiers={["danger"]}
                      anchor
                      to={"/criar-uma-conta/fim"}
                    >
                      Não. Preciso corrigir
                    </Button>
                  </>
                ) : null}
              </ButtonWrapper>
            </Form>
          )}
        </Formik>
      </FormStep>

      <FormStep show={formStep === "senha"}>
        <Formik
          initialValues={{
            email: formValues.email,
            password: formValues.password ?? "",
            confirmPassword: formValues.confirmPassword ?? "",
          }}
          validationSchema={passwordSchema}
          onSubmit={handleNext}
        >
          {(props) => (
            <Form>
              <div className={loginStyles.body}>
                <div className={loginStyles.textGroup}>
                  <h1 className={loginStyles.title}>
                    <b>Muito bom!</b> Agora, você precisa de email e senha para
                    acessar a plataforma. Informe abaixo:
                  </h1>
                </div>

                <FormRow>
                  <FormLabel htmlFor="email">*Email</FormLabel>
                  <FormControl
                    id="email"
                    name="email"
                    type="email"
                    value={props.values.email}
                    invalid={props.errors.email && props.touched.email}
                    placeholder="Insira seu e-mail"
                    autoComplete="username"
                    readOnly
                  />
                  <ErrorMessage name="email">
                    {(msg) => <FormFeedback invalid={true}>{msg}</FormFeedback>}
                  </ErrorMessage>
                  {errors?.email && (
                    <ErrorMessage name="email">
                      {
                        <FormFeedback invalid={true}>
                          {errors.email}
                        </FormFeedback>
                      }
                    </ErrorMessage>
                  )}
                </FormRow>
                <FormRow>
                  <FormLabel htmlFor="password">*Senha</FormLabel>
                  <PasswordStrength
                    show={showPasswordStrength}
                    password={props.values.password}
                    validPassword={setPasswordIsStrong}
                  >
                    <FormControl
                      id="password"
                      name="password"
                      type={props.values.showPassword ? "text" : "password"}
                      onChange={props.handleChange}
                      onFocus={() => setShowPasswordStrength(true)}
                      onBlur={() => setShowPasswordStrength(false)}
                      value={props.values.password}
                      invalid={props.errors.password && props.touched.password}
                      placeholder="Insira sua senha"
                      ref={passwordRef}
                      autoComplete="new-password"
                    />
                  </PasswordStrength>
                  <FormField>
                    <Field type="checkbox" name="showPassword" />
                    Mostrar senha
                  </FormField>
                  <ErrorMessage name="password">
                    {(msg) => <FormFeedback invalid={true}>{msg}</FormFeedback>}
                  </ErrorMessage>
                </FormRow>

                <FormRow>
                  <FormLabel htmlFor="confirmPassword">
                    *Confirme sua senha
                  </FormLabel>
                  <FormControl
                    id="confirmPassword"
                    name="confirmPassword"
                    type={
                      props.values.showConfirmPassword ? "text" : "password"
                    }
                    onChange={props.handleChange}
                    value={props.values.confirmPassword}
                    invalid={
                      props.errors.confirmPassword &&
                      props.touched.confirmPassword
                    }
                    placeholder="Insira sua senha"
                    ref={confirmPasswordRef}
                    autoComplete="new-password"
                  />
                  <FormField>
                    <Field type="checkbox" name="showConfirmPassword" />
                    Mostrar senha
                  </FormField>
                  <ErrorMessage name="confirmPassword">
                    {(msg) => <FormFeedback invalid={true}>{msg}</FormFeedback>}
                  </ErrorMessage>
                </FormRow>
              </div>
              <div
                className={`${styles["email-info"]} ${loginStyles.box} ${loginStyles.description}`}
              >
                <HiOutlineLightBulb
                  className={`${styles["email-info-icon"]}`}
                />

                <p className="mb-0">
                  Este é seu email institucional cadastrado no iSeduc. Ele será
                  usado para acessar a plataforma.
                </p>
              </div>
              <ButtonWrapper>
                <Button
                  modifiers={["primary"]}
                  type="submit"
                  style={{ minWidth: 134 }}
                  disabled={
                    props.isSubmitting ||
                    !props.values.email ||
                    !props.values.password ||
                    !props.values.confirmPassword ||
                    !passwordIsStrong ||
                    loading
                  }
                >
                  Próximo
                </Button>
              </ButtonWrapper>
            </Form>
          )}
        </Formik>
      </FormStep>

      <FormStep show={formStep === "termos"}>
        <Formik
          initialValues={{ terms: formValues.terms ?? true }}
          validationSchema={termsSchema}
          onSubmit={handleNext}
        >
          {(props) => (
            <Form>
              <div className={loginStyles.body}>
                <div className={loginStyles.textGroup}>
                  <h1 className={loginStyles.title}>
                    <strong>Por fim,</strong> nosso termos de uso e política de
                    privacidade
                  </h1>
                </div>

                <FormRow>
                  <FormTerms />
                </FormRow>

                <FormRow>
                  <FormField round>
                    <Field type="checkbox" name="terms" />
                    <span>
                      Ao clicar em “Criar conta”, você concorda com os{" "}
                      <a
                        href="/termos-de-uso"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        termos de uso
                      </a>{" "}
                      e{" "}
                      <a
                        href="/politica-de-privacidade"
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        política de privacidade
                      </a>
                    </span>
                  </FormField>
                </FormRow>
              </div>

              <ButtonWrapper>
                <Button
                  modifiers={["primary"]}
                  type="submit"
                  style={{ minWidth: 134 }}
                  disabled={
                    !props.values.terms || props.isSubmitting || loading
                  }
                >
                  Criar minha conta
                </Button>
              </ButtonWrapper>
            </Form>
          )}
        </Formik>
      </FormStep>

      <FormStep show={formStep === "conta-criada"}>
        <Formik>
          {(props) => (
            <Form>
              <div className={loginStyles.body}>
                <FormRow>
                  <strong style={{ fontWeight: "bold" }}>
                    Tudo certo. Sua conta foi criada!
                  </strong>
                  <p>
                    Enviamos um e-mail para você. Acesse-o para ativar sua conta
                  </p>

                  <strong
                    style={{
                      fontWeight: "bold",
                      marginTop: 24,
                      color: "var(--color-primary)",
                    }}
                  >
                    É muito bom ter você com a gente!
                  </strong>
                </FormRow>
              </div>
            </Form>
          )}
        </Formik>
      </FormStep>
      <FormStep show={formStep === "fim"}>
        <Formik>
          {(props) => (
            <Form>
              <div className={loginStyles.body}>
                <FormRow>
                  <p style={{ fontWeight: "bold" }}>Tudo bem!</p>
                  <p>
                    Vá até a sua escola para corrigir seus dados. Depois disso,
                    é só voltar aqui e finalizar seu cadastro para ter acesso
                    aos conteúdos e começar a estudar!
                  </p>
                  <p style={{ marginTop: 12 }}>Estaremos te esperando.</p>
                </FormRow>
              </div>
            </Form>
          )}
        </Formik>
      </FormStep>
    </LoginLayout>
  );
}
